import { useCallback, useState } from "react";
import { SupervisorTeamClient, SupervisorTeamMember } from "../api/contracts";

export const TEAM_CLINIC_QUERY_PARAMETER = "clinicId";

export function uniqueSupervisorTeamMembers(
  members: readonly SupervisorTeamMember[],
): SupervisorTeamMember[] {
  const byId = new Map<string, SupervisorTeamMember>();
  members.forEach((member) => {
    const existing = byId.get(member.id);
    if (!existing) {
      byId.set(member.id, { ...member, memberships: [...member.memberships] });
      return;
    }
    const memberships = new Map(
      existing.memberships.map((membership) => [
        `${membership.clinicId}:${membership.membershipRole}`,
        membership,
      ]),
    );
    member.memberships.forEach((membership) => {
      memberships.set(
        `${membership.clinicId}:${membership.membershipRole}`,
        membership,
      );
    });
    byId.set(member.id, {
      ...existing,
      isCurrentUser: existing.isCurrentUser || member.isCurrentUser,
      memberships: [...memberships.values()],
    });
  });
  return [...byId.values()];
}

export function uniqueSupervisorTeamClients(
  clients: readonly SupervisorTeamClient[],
): SupervisorTeamClient[] {
  const byId = new Map<string, SupervisorTeamClient>();
  clients.forEach((client) => {
    const existing = byId.get(client.id);
    if (!existing) {
      byId.set(client.id, {
        ...client,
        assignedTherapists: [...client.assignedTherapists],
      });
      return;
    }
    const assignedTherapists = new Map(
      existing.assignedTherapists.map((therapist) => [therapist.id, therapist]),
    );
    client.assignedTherapists.forEach((therapist) =>
      assignedTherapists.set(therapist.id, therapist),
    );
    byId.set(client.id, {
      ...existing,
      assignedTherapists: [...assignedTherapists.values()],
    });
  });
  return [...byId.values()];
}

export function readTeamClinicId(search?: string): string {
  const currentSearch =
    search ?? (typeof window === "undefined" ? "" : window.location.search);
  return (
    new URLSearchParams(currentSearch)
      .get(TEAM_CLINIC_QUERY_PARAMETER)
      ?.trim() ?? ""
  );
}

export function withTeamClinicScope(path: string, clinicId: string): string {
  const origin =
    typeof window === "undefined"
      ? "https://portal.local"
      : window.location.origin;
  const destination = new URL(path, origin);
  if (typeof window !== "undefined") {
    const current = new URL(window.location.href);
    current.searchParams.forEach((value, key) => {
      if (key !== "clinicianCode" && !destination.searchParams.has(key)) {
        destination.searchParams.set(key, value);
      }
    });
  }
  destination.searchParams.delete("clinicianCode");
  if (clinicId.trim()) {
    destination.searchParams.set(TEAM_CLINIC_QUERY_PARAMETER, clinicId.trim());
  } else {
    destination.searchParams.delete(TEAM_CLINIC_QUERY_PARAMETER);
  }
  return `${destination.pathname}${destination.search}${destination.hash}`;
}

function replaceCurrentClinicScope(clinicId: string): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.delete("clinicianCode");
  if (clinicId.trim()) {
    url.searchParams.set(TEAM_CLINIC_QUERY_PARAMETER, clinicId.trim());
  } else {
    url.searchParams.delete(TEAM_CLINIC_QUERY_PARAMETER);
  }
  window.history.replaceState(
    {},
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}

export function useTeamClinicScope(): {
  clinicId: string;
  setClinicId: (clinicId: string) => void;
} {
  const [clinicId, setClinicIdState] = useState(() => readTeamClinicId());
  const setClinicId = useCallback((nextClinicId: string) => {
    const normalizedClinicId = nextClinicId.trim();
    setClinicIdState(normalizedClinicId);
    replaceCurrentClinicScope(normalizedClinicId);
  }, []);
  return { clinicId, setClinicId };
}
